import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useRef, useState } from 'react';
import {
  studyRequiredDocuments, ai, bulkClassification,
  type RequiredDocumentChecklist, type RequiredDocumentItem,
  type RequiredDocumentStatus, type DocumentSource,
  type BulkJobStatus,
} from '@/api/endpoints';
import { Icon } from './Icon';
import { BulkClassificationModal } from './BulkClassificationModal';
import { SearchableSelect } from '@/components/SearchableSelect';
import { validateUploadSize, type UploadProgress } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';

function fmtBulkElapsed(startIso?: string): string {
  if (!startIso) return '';
  const ms = Date.now() - new Date(startIso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m ${s % 60}s`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

const STATUS_OPTIONS: { value: RequiredDocumentStatus; label: string; pill: string }[] = [
  { value: 'PENDING',         label: 'Oczekuje',    pill: '' },
  { value: 'RECEIVED',        label: 'Mamy',        pill: 'pill-green' },
  { value: 'NOT_APPLICABLE',  label: 'Nie dotyczy', pill: 'pill-amber' },
  { value: 'WAIVED',          label: 'Zwolniono',   pill: 'pill-amber' },
];

const SOURCE_LABEL: Record<DocumentSource, string> = { FIRM: 'Firma', CLIENT: 'Klient' };

/** Formatuje ISO timestamp w formacie pl-PL "07.05.2026, 11:23". Pusta gdy null/niepoprawne. */
function formatDateTimeLocal(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pl-PL', { dateStyle: 'short', timeStyle: 'short' }).format(d);
}

/**
 * Karta z checklistą wymaganych dokumentów wejściowych dla danego Study.
 * Pozycje są generowane automatycznie z RequiredDocumentTemplate przy tworzeniu Study.
 * Geolog: zmienia status, dodaje pliki, ustawia termin/notatkę.
 */
export function StudyRequiredDocsCard({ studyId, onDraftsGenerated, readOnly = false }: {
  studyId: number;
  /** Wywoływane po wygenerowaniu draftów AI — pozwala parentowi (StudyDetail) odświeżyć listę. */
  onDraftsGenerated?: () => void;
  /** Tryb tylko do odczytu — używany dla zakończonych/anulowanych opracowań. */
  readOnly?: boolean;
}) {
  const [data, setData] = useState<RequiredDocumentChecklist | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editingFields, setEditingFields] = useState<RequiredDocumentItem | null>(null);

  const reload = () => {
    setErr(null);
    studyRequiredDocuments.list(studyId).then(setData).catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [studyId]);

  const onChangeStatus = async (item: RequiredDocumentItem, newStatus: RequiredDocumentStatus) => {
    if (newStatus === 'WAIVED' && !item.note) {
      const note = prompt('Status „Zwolniono" wymaga uzasadnienia. Podaj powód:');
      if (!note || !note.trim()) return;
      setBusyId(item.id);
      try { await studyRequiredDocuments.changeStatus(item.id, 'WAIVED', note.trim()); reload(); }
      catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
      finally { setBusyId(null); }
      return;
    }
    setBusyId(item.id);
    try { await studyRequiredDocuments.changeStatus(item.id, newStatus, null); reload(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusyId(null); }
  };

  /** Postęp uploadu wielu plików — pokazywany w wierszu pozycji. */
  const [uploadProgress, setUploadProgress] = useState<{ id: number; current: number; total: number; name: string } | null>(null);
  /** Postęp bajtowy bieżąco wysyłanego pliku (0..100). null = brak wysyłki. */
  const [uploadPct, setUploadPct] = useState<number | null>(null);

  const onUpload = async (item: RequiredDocumentItem, files: File[]) => {
    if (files.length === 0) return;
    // Pomijamy "pseudo-pliki" które przeglądarka zwraca dla folderów (rozmiar 0 i pusty type).
    // Foldery rozpakowywane są w ItemRow przez webkitGetAsEntry — tu już dostajemy listę realnych plików.
    const real = files.filter(f => f.size > 0 || f.type !== '');
    if (real.length === 0) {
      setErr('Próba uploadu pustych plików lub folderu bez zawartości.');
      return;
    }
    // Limit rozmiaru: 100 MB na plik — blokujemy zanim cokolwiek poleci na serwer.
    const sizeErr = validateUploadSize(real);
    if (sizeErr) { setErr(sizeErr); return; }
    setBusyId(item.id); setErr(null);
    let lastError: string | null = null;
    let completed = 0;
    let duplicates = 0;
    // Dedup po stronie klienta: jeśli user wybrał ten sam plik dwa razy w tej akcji, też pomijamy.
    // Klucz = name + size + lastModified (tani fingerprint przed bytes-hash).
    const seenLocally = new Set<string>();
    try {
      for (let i = 0; i < real.length; i++) {
        const f = real[i];
        const localKey = `${f.name}|${f.size}|${f.lastModified}`;
        if (seenLocally.has(localKey)) {
          duplicates++;
          continue;
        }
        seenLocally.add(localKey);
        setUploadProgress({ id: item.id, current: i + 1, total: real.length, name: f.name });
        setUploadPct(0); // reset paska bajtowego dla bieżącego pliku
        try {
          const res = await studyRequiredDocuments.uploadFile(item.id, f, (p: UploadProgress) => setUploadPct(p.percent));
          if (res.duplicate) duplicates++;
          else completed++;
        } catch (e) {
          lastError = `${f.name}: ${e instanceof Error ? e.message : String(e)}`;
          // Nie przerywamy — uploadujemy resztę plików, błąd raportujemy na końcu.
        }
      }
    } finally {
      setUploadProgress(null);
      setBusyId(null);
      setUploadPct(null);
    }
    if (lastError) {
      setErr(`Wgrano ${completed}/${real.length}. Ostatni błąd — ${lastError}`);
    } else if (duplicates > 0) {
      setErr(`Wgrano ${completed} plików, pominięto ${duplicates} duplikat${duplicates === 1 ? '' : duplicates < 5 ? 'y' : 'ów'}.`);
    }
    reload();
  };

  const onUnlink = async (item: RequiredDocumentItem, fileId: number) => {
    if (!confirm('Odpiąć plik od tej pozycji? Plik pozostanie w archiwum.')) return;
    setBusyId(item.id);
    try { await studyRequiredDocuments.unlinkFile(item.id, fileId); reload(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusyId(null); }
  };

  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResumeJobId, setBulkResumeJobId] = useState<string | null>(null);
  // Status aktywnego bulk-jobu — żywy dopóki coś się dzieje LUB są propozycje czekające na decyzję.
  type ActiveBulk = Partial<BulkJobStatus> & { active: boolean; jobId?: string };
  const [activeBulk, setActiveBulk] = useState<ActiveBulk | null>(null);
  const [, forceTick] = useState(0); // odświeża tylko licznik czasu

  useEffect(() => {
    let mounted = true;
    const poll = async () => {
      try {
        const res = await bulkClassification.activeForStudy(studyId);
        if (!mounted) return;
        setActiveBulk(res.active ? (res as ActiveBulk) : null);
      } catch { /* nonfatal — banner się nie pojawi */ }
      if (mounted) setTimeout(poll, 3000);
    };
    poll();
    return () => { mounted = false; };
  }, [studyId]);

  // Tick do odświeżania licznika upływającego czasu w banerze
  useEffect(() => {
    if (!activeBulk?.startedAt) return;
    const id = setInterval(() => forceTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [activeBulk?.startedAt]);
  const onGenerateAllDrafts = async () => {
    if (!confirm('Wygenerować draft opracowania (wszystkie sekcje) na podstawie kompletu dokumentów + bazy RAG? Trwa zwykle 1-3 minuty.')) return;
    setGeneratingDraft(true); setErr(null);
    try {
      const res = await ai.generateAllDrafts(studyId);
      const ok = res.generatedSections.length;
      const failed = res.failedSections.length;
      // Powiadom rodzica (StudyDetail) żeby przeładował listę draftów AI
      onDraftsGenerated?.();
      if (failed === 0) {
        alert(`✓ Wygenerowano ${ok} sekcji draftu opracowania:\n` + res.generatedSections.map(s => '• ' + s).join('\n')
              + '\n\nDrafty są dostępne w karcie "Drafty AI" tego opracowania.');
      } else {
        alert(`Wygenerowano ${ok}/${res.requested} sekcji. Niepowodzenia: ${res.failedSections.join(', ')}.`
              + (res.firstError ? '\n\nPierwszy błąd: ' + res.firstError : ''));
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setGeneratingDraft(false);
    }
  };

  const onRefresh = async () => {
    try {
      const res = await studyRequiredDocuments.refresh(studyId);
      if (res.added > 0) {
        alert(`Dodano ${res.added} nowych pozycji ze słownika.`);
        reload();
      } else {
        alert('Wszystkie pozycje słownika są już w checkliście.');
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  if (err) return (
    <div className="card mb-4"><div className="card-body text-red-500 text-[12px]">Błąd: {err}</div></div>
  );
  if (!data) return (
    <div className="card mb-4"><div className="card-body text-ink-400">Ładowanie checklisty…</div></div>
  );

  const annual = data.annualIntakeM3;
  const completionPercent = data.total > 0
    ? Math.round(((data.received + data.notApplicable + data.waived) / data.total) * 100)
    : 0;

  return (
    <div className="card mb-4">
      <div className="card-head">
        <div className="card-title">Wymagane dokumenty wejściowe</div>
        <span className="ml-3 text-[12px] font-mono text-ink-700">
          {data.received}/{data.total} mamy
          {data.pending > 0 && <span className="text-amber-700"> · {data.pending} oczekuje</span>}
          {(data.notApplicable + data.waived) > 0 &&
            <span className="text-ink-400"> · {data.notApplicable + data.waived} n-d/zwolnionych</span>}
        </span>
        {annual != null && (
          <span className="ml-3 text-[11px] text-ink-500"
                title="Suma średniodobowych poborów wszystkich studni ujęcia × 365">
            Roczny pobór ujęcia: <span className="font-mono">{annual.toLocaleString('pl-PL')}</span> m³/rok
          </span>
        )}
        <span className="ml-auto text-[12px] text-ink-500 font-mono">{completionPercent}%</span>
        {!readOnly && (
          <>
            <button onClick={() => setBulkOpen(true)} className="btn btn-secondary btn-sm ml-2"
                    title="Wgraj cały katalog z plikami — AI dopasuje każdy do pozycji checklisty">
              <Icon name="folder" className="w-3 h-3"/> Wgraj katalog (AI)
            </button>
            {(() => {
              const canGenerate = data.pending === 0 && data.total > 0 && !generatingDraft;
              const tooltip = data.total === 0
                ? 'Brak pozycji checklisty — nic do wygenerowania'
                : data.pending > 0
                  ? `Najpierw zamknij ${data.pending} pozycji w statusie "Oczekuje" (Mamy / Nie dotyczy / Zwolniono)`
                  : 'Generuje draft wszystkich sekcji opracowania (Wstęp, Lokalizacja, Metodyka, …) z plików checklisty + bazy RAG';
              return (
                <button onClick={onGenerateAllDrafts}
                        disabled={!canGenerate}
                        title={tooltip}
                        className={'btn btn-sm ml-2 ' + (canGenerate ? 'btn-primary' : 'btn-secondary opacity-60')}>
                  {generatingDraft ? (
                    <>
                      <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                      {' '}Generowanie…
                    </>
                  ) : (
                    <>
                      <Icon name="sparkles" className="w-3 h-3"/> Generuj draft opracowania (AI)
                    </>
                  )}
                </button>
              );
            })()}
            <button onClick={onRefresh} className="btn btn-secondary btn-sm ml-2"
                    title="Dociąga pozycje słownika dodane po utworzeniu opracowania">
              <Icon name="plus" className="w-3 h-3"/> Odśwież ze słownika
            </button>
          </>
        )}
      </div>
      {activeBulk && (
        <div className="px-4 py-2 border-b border-water-200 bg-water-50/60">
          <BulkJobBanner
            status={activeBulk}
            onOpen={() => { setBulkResumeJobId(activeBulk.jobId ?? null); setBulkOpen(true); }}
          />
        </div>
      )}
      <div className="card-body !p-0">
        <table className="table-hw">
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th>Pozycja</th>
              <th className="w-24">Źródło</th>
              <th className="w-36">Status</th>
              <th>Pliki</th>
              <th className="w-28">Termin</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(item => (
              <ItemRow
                key={item.id}
                item={item}
                busy={busyId === item.id}
                readOnly={readOnly}
                progress={uploadProgress?.id === item.id
                  ? { current: uploadProgress.current, total: uploadProgress.total, name: uploadProgress.name }
                  : null}
                uploadPct={uploadProgress?.id === item.id ? uploadPct : null}
                onChangeStatus={onChangeStatus}
                onUpload={onUpload}
                onUnlink={onUnlink}
                onEdit={() => setEditingFields(item)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {editingFields && (
        <ItemFieldsModal
          initial={editingFields}
          onClose={() => setEditingFields(null)}
          onSaved={() => { setEditingFields(null); reload(); }}
        />
      )}

      <BulkClassificationModal
        open={bulkOpen}
        studyId={studyId}
        checklistItems={data.items}
        resumeJobId={bulkResumeJobId}
        onClose={() => { setBulkOpen(false); setBulkResumeJobId(null); reload(); }}
        onChanged={reload}
      />
    </div>
  );
}

/** Mały inline spinner (CSS, bez ikony). Domyślnie 14px — można nadpisać klasami rozmiaru. */
function Spinner({ className = '' }: { className?: string }) {
  return (
    <span
      className={'inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin ' + className}
      aria-label="Ładowanie"
      role="status"
    />
  );
}

/** Rekursywnie zbiera pliki z FileSystemEntry (webkit drag-drop API). */
async function readEntry(entry: FileSystemEntry): Promise<File[]> {
  if (entry.isFile) {
    return new Promise<File[]>((resolve, reject) => {
      (entry as FileSystemFileEntry).file(
        f => resolve([f]),
        err => reject(err),
      );
    });
  }
  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const collected: File[] = [];
    // readEntries może zwrócić tylko część dzieci za jednym razem — wołamy w pętli aż dostaniemy pustą tablicę.
    const readBatch = (): Promise<FileSystemEntry[]> => new Promise((res, rej) => {
      reader.readEntries(entries => res(Array.from(entries)), err => rej(err));
    });
    while (true) {
      const batch = await readBatch();
      if (batch.length === 0) break;
      for (const child of batch) {
        const sub = await readEntry(child);
        collected.push(...sub);
      }
    }
    return collected;
  }
  return [];
}

function ItemRow({ item, busy, readOnly = false, progress, uploadPct, onChangeStatus, onUpload, onUnlink, onEdit }: {
  item: RequiredDocumentItem;
  busy: boolean;
  readOnly?: boolean;
  progress: { current: number; total: number; name: string } | null;
  /** Postęp bajtowy bieżąco wysyłanego pliku (0..100); null gdy nic nie leci. */
  uploadPct: number | null;
  onChangeStatus: (item: RequiredDocumentItem, status: RequiredDocumentStatus) => void;
  onUpload: (item: RequiredDocumentItem, files: File[]) => void;
  onUnlink: (item: RequiredDocumentItem, fileId: number) => void;
  onEdit: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const statusInfo = STATUS_OPTIONS.find(o => o.value === item.status);
  const [dragOver, setDragOver] = useState(false);

  const showThresholdHint = item.qThresholdM3Year != null && item.status === 'NOT_APPLICABLE';

  const handleDragOver = (e: React.DragEvent) => {
    if (busy || readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    if (!dragOver) setDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (busy || readOnly) return;

    // Foldery na drop są dostępne tylko przez webkitGetAsEntry (DataTransferItem API).
    // dataTransfer.files dla folderu zwraca pseudo-File z size:0 → backend by go odrzucił.
    const items = e.dataTransfer.items ? Array.from(e.dataTransfer.items) : [];
    const collected: File[] = [];
    for (const it of items) {
      if (it.kind !== 'file') continue;
      const entry = (it as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntry | null }).webkitGetAsEntry?.();
      if (entry) {
        try {
          const sub = await readEntry(entry);
          collected.push(...sub);
        } catch {
          // Fallback dla rzadkich przypadków — weź jako pojedynczy plik
          const f = it.getAsFile();
          if (f) collected.push(f);
        }
      } else {
        const f = it.getAsFile();
        if (f) collected.push(f);
      }
    }
    // Fallback gdy DataTransferItem API nie jest dostępne (starsze przeglądarki)
    if (collected.length === 0 && e.dataTransfer.files?.length) {
      collected.push(...Array.from(e.dataTransfer.files));
    }
    if (collected.length > 0) onUpload(item, collected);
  };

  // Wiersz aktywnie uploadujący traci opacity-50 (chcemy widzieć progress wyraźnie); zostaje
  // tylko subtelne tło. Inne busy operacje (zmiana statusu/odpinanie) zachowują dim-out.
  const isUploading = !!progress;
  return (
    <tr className={
          (busy && !isUploading ? 'opacity-50 ' : '')
        + (isUploading ? 'bg-water-50/60 ' : '')
        + (dragOver ? 'bg-water-50 outline outline-2 outline-water-400' : '')}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
      <td className="text-center">
        {isUploading ? (
          <Spinner className="text-water-600"/>
        ) : (
          <span className={'inline-block w-3 h-3 rounded-full ' + (
            item.status === 'RECEIVED' ? 'bg-green-500' :
            item.status === 'PENDING' ? 'bg-amber-400' :
            'bg-ink-300'
          )} title={statusInfo?.label}/>
        )}
      </td>
      <td>
        <div className="font-mono text-[10px] text-ink-500">{item.code}</div>
        <div className="text-[13px] text-ink-900">{item.name}</div>
        {item.legalBasis && <div className="text-[11px] text-ink-500 mt-0.5">📋 {item.legalBasis}</div>}
        {item.conditionText && <div className="text-[11px] text-amber-700 mt-0.5">⚠ {item.conditionText}</div>}
        {item.qThresholdM3Year != null && (
          <div className="text-[11px] text-ink-500 mt-0.5">
            Próg: <span className="font-mono">{item.qThresholdM3Year.toLocaleString('pl-PL')}</span> m³/rok
          </div>
        )}
        {item.note && <div className="text-[11px] italic text-ink-600 mt-1">💬 {item.note}</div>}
      </td>
      <td>
        <span className={'pill ' + (item.source === 'FIRM' ? 'pill-green' : '')}>
          {SOURCE_LABEL[item.source]}
        </span>
      </td>
      <td>
        <SearchableSelect
          value={item.status}
          onChange={v => onChangeStatus(item, v as RequiredDocumentStatus)}
          disabled={busy || readOnly}
          className="select-sm text-[12px]"
        >
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </SearchableSelect>
        {showThresholdHint && (
          <div className="text-[10px] text-ink-400 mt-0.5">auto: poniżej progu Q</div>
        )}
      </td>
      <td>
        {item.files.length === 0 ? (
          <span className={'text-[11px] italic ' + (dragOver ? 'text-water-700 font-semibold' : 'text-ink-400')}>
            {dragOver ? '⤓ Upuść plik tutaj' : 'brak plików · przeciągnij plik tutaj'}
          </span>
        ) : (
          <ul className="text-[11px] m-0 p-0 list-none">
            {item.files.map(f => (
              <li key={f.id} className="flex items-center gap-1.5 mb-0.5">
                <Icon name="file-text" className="w-3 h-3 text-ink-500 shrink-0"/>
                <a
                  href={`/api/archive/documents/${f.id}/download?disposition=inline`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-ink-800 hover:text-water-700 hover:underline truncate"
                  style={{ maxWidth: 220 }}
                  title={'Podgląd: ' + f.name}>
                  {f.name}
                </a>
                {f.sizeBytes != null && (
                  <span className="text-ink-400 font-mono">
                    ({Math.round(f.sizeBytes / 1024)} kB)
                  </span>
                )}
                {f.addedAt && (
                  <span className="text-ink-400 font-mono"
                        title={'Dodano: ' + formatDateTimeLocal(f.addedAt)}>
                    · {formatDateTimeLocal(f.addedAt)}
                  </span>
                )}
                <a
                  href={`/api/archive/documents/${f.id}/download?disposition=inline`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-water-600 hover:text-water-800 p-0.5"
                  title="Podgląd w nowej karcie">
                  <Icon name="eye" className="w-3 h-3"/>
                </a>
                <a
                  href={`/api/archive/documents/${f.id}/download?disposition=attachment`}
                  download={f.name}
                  className="text-water-600 hover:text-water-800 p-0.5"
                  title="Pobierz plik">
                  <Icon name="download" className="w-3 h-3"/>
                </a>
                {!readOnly && (
                  <button onClick={() => onUnlink(item, f.id)} disabled={busy}
                          className="text-red-500 hover:text-red-700 p-0.5"
                          title="Odepnij plik (pozostaje w archiwum)">
                    <Icon name="x" className="w-3 h-3"/>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <input ref={fileInputRef} type="file" hidden multiple
               onChange={e => {
                 const files = Array.from(e.target.files ?? []);
                 if (files.length > 0) { onUpload(item, files); e.target.value = ''; }
               }}/>
        <div className="flex items-center gap-2 mt-1">
          {!readOnly && (
            <button onClick={() => fileInputRef.current?.click()} disabled={busy}
                    className="btn btn-secondary btn-sm">
              <Icon name="plus" className="w-3 h-3"/> Wgraj plik(i)
            </button>
          )}
          {progress && (
            <span className="text-[11px] text-water-600 flex items-center gap-1.5">
              <Spinner className="text-water-500"/>
              {progress.current}/{progress.total}: <span className="font-mono truncate max-w-[180px]">{progress.name}</span>
            </span>
          )}
          {uploadPct !== null && (
            <UploadProgressBar percent={uploadPct} label="Wysyłanie pliku…" compact className="flex-1 min-w-[160px]"/>
          )}
        </div>
      </td>
      <td className="font-mono text-[11px] text-ink-700">
        {item.dueDate ?? <span className="text-ink-400">—</span>}
      </td>
      <td className="text-right">
        {!readOnly && (
          <button onClick={onEdit} className="text-water-600 hover:text-water-800 p-1"
                  title="Edytuj notatkę / termin / źródło">
            <Icon name="edit" className="w-3.5 h-3.5"/>
          </button>
        )}
      </td>
    </tr>
  );
}

function ItemFieldsModal({ initial, onClose, onSaved }: {
  initial: RequiredDocumentItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [source, setSource] = useState<DocumentSource>(initial.source);
  const [note, setNote] = useState<string>(initial.note ?? '');
  const [dueDate, setDueDate] = useState<string>(initial.dueDate ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await studyRequiredDocuments.updateFields(initial.id, {
        source, note: note || null, dueDate: dueDate || null,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { source, note, dueDate }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-2xl max-w-[600px] w-full" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
          <h2 className="text-[16px] font-semibold m-0">Pozycja: {initial.code}</h2>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>
        <div className="p-5 space-y-3">
          {err && <div className="text-red-500 text-[12px]">{err}</div>}

          <div className="text-[13px] text-ink-700 mb-2"><strong>{initial.name}</strong></div>
          {initial.description && (
            <div className="text-[12px] text-ink-500 italic">{initial.description}</div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Kto dostarcza">
              <SearchableSelect value={source} onChange={v => setSource(v as DocumentSource)}>
                <option value="FIRM">Firma</option>
                <option value="CLIENT">Klient</option>
              </SearchableSelect>
            </Field>
            <Field label="Termin (klient ma dostarczyć do)">
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="input font-mono"/>
            </Field>
          </div>

          <Field label="Notatka">
            <textarea value={note} onChange={e => setNote(e.target.value)}
                      placeholder="np. klient potwierdził dostarczenie do 15.05" rows={3}
                      className="input min-h-[60px]"/>
          </Field>
        </div>
        <div className="border-t border-ink-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={requestClose} disabled={busy} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={save} disabled={busy} className="btn btn-primary btn-sm">
            {busy ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">{label}</span>
      {children}
    </div>
  );
}

/**
 * Stały baner widoczny w karcie checklisty gdy w tle działa bulk-import lub czekają
 * propozycje do akceptacji. Persists across navigation — fetchowany niezależnie od modalu.
 */
function BulkJobBanner({
  status,
  onOpen,
}: {
  status: Partial<BulkJobStatus> & { active: boolean; jobId?: string };
  onOpen: () => void;
}) {
  const elapsed = fmtBulkElapsed(status.startedAt);
  const phase = status.phase;
  const isIngesting = phase === 'ingesting';
  const isClassifying = phase === 'classifying';
  const isCancelling = phase === 'cancelling';
  const isWorking = isIngesting || isClassifying || isCancelling;
  const ingestPct = status.totalFiles && status.totalFiles > 0
    ? Math.min(100, Math.round(((status.ingested ?? 0) / status.totalFiles) * 100))
    : 0;
  const total = status.total ?? 0;
  const processing = status.processing ?? 0;
  const proposed = status.proposed ?? 0;
  const classifyDone = total - processing;
  const classifyPct = total > 0 ? Math.min(100, Math.round((classifyDone / total) * 100)) : 0;

  let title: string;
  let icon: 'sparkles' | 'check' | 'bell';
  let tone: 'water' | 'amber' | 'green';
  if (isCancelling) {
    title = 'Bulk import — anulowanie (czekam na zakończenie aktualnego pliku)';
    icon = 'sparkles'; tone = 'amber';
  } else if (isIngesting) {
    title = `Bulk import w toku: wgrywanie i ekstrakcja tekstu (${status.ingested ?? 0}/${status.totalFiles ?? '?'} plików)`;
    icon = 'sparkles'; tone = 'water';
  } else if (isClassifying) {
    title = `Bulk import w toku: klasyfikacja AI (${classifyDone}/${total} plików)`;
    icon = 'sparkles'; tone = 'water';
  } else if (proposed > 0) {
    title = `Bulk import zakończony: ${proposed} propozycji czeka na decyzję`;
    icon = 'bell'; tone = 'amber';
  } else if (phase === 'failed') {
    title = `Bulk import zakończony błędem`;
    icon = 'x' as 'check'; tone = 'amber';
  } else {
    title = `Bulk import: ${total} plików`;
    icon = 'check'; tone = 'green';
  }

  const toneClass =
    tone === 'water' ? 'text-water-700' :
    tone === 'amber' ? 'text-amber-700' :
    'text-green-700';

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className={'flex items-center gap-2 ' + toneClass}>
        {isWorking
          ? <span className="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"/>
          : <Icon name={icon} className="w-3.5 h-3.5"/>}
        <span className="text-[12px] font-semibold">{title}</span>
      </div>
      {isWorking && (
        <div className="flex-1 min-w-[160px] max-w-[420px]">
          <div className="w-full bg-ink-100 rounded-full h-1.5 overflow-hidden">
            <div className="bg-water-500 h-full transition-all"
                 style={{ width: (isIngesting ? ingestPct : classifyPct) + '%' }}/>
          </div>
        </div>
      )}
      {elapsed && <span className="text-[11px] text-ink-500 font-mono">⏱ {elapsed}</span>}
      {isIngesting && status.currentFilename && (
        <span className="text-[11px] text-ink-500 truncate max-w-[260px]" title={status.currentFilename}>
          <span className="font-mono">{status.currentFilename}</span>
        </span>
      )}
      <button onClick={onOpen} className="btn btn-primary btn-sm ml-auto">
        <Icon name="folder" className="w-3 h-3"/>
        {isWorking ? 'Pokaż okno postępu' : 'Otwórz okno akceptacji'}
      </button>
    </div>
  );
}
